"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isMarketingAllowed } from "@/lib/analytics-consent";
import { jsonForInlineScript } from "@/lib/ga4-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onMarketingAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

declare global {
  interface Window {
    twq?: (...args: unknown[]) => void;
  }
}

const X_PIXEL_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function XPixelLoader({ pixelId }: { pixelId: string | undefined }) {
  const pathname = usePathname();
  const [enabled, setEnabled] = useState(false);
  const [generation, setGeneration] = useState(0);
  const previousPath = useRef<string | null>(null);
  const wasEnabled = useRef(false);
  const prod = isProdPublicTrackingEnabled();
  const id = pixelId?.trim() ?? "";
  const validId = X_PIXEL_ID.test(id);

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onMarketingAllowanceChanged(false);
      wasEnabled.current = false;
      previousPath.current = null;
      setEnabled(false);
      return;
    }
    if (!wasEnabled.current) setGeneration((value) => value + 1);
    wasEnabled.current = true;
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!prod || !validId) return;
    applyAllowance(isMarketingAllowed());
    const onPreferencesChanged = () => applyAllowance(isMarketingAllowed());
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
  }, [applyAllowance, prod, validId]);

  useEffect(() => {
    if (!enabled) return;
    if (previousPath.current === null) {
      previousPath.current = pathname;
      return;
    }
    if (previousPath.current !== pathname && typeof window.twq === "function") {
      window.twq("track", "PageView");
      previousPath.current = pathname;
    }
  }, [enabled, pathname]);

  if (!prod || !validId || !enabled) return null;
  const safeId = jsonForInlineScript(id);
  return (
    <Script id={`mot-x-pixel-${generation}`} strategy="afterInteractive">
      {`!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments)},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');twq('config',${safeId});twq('track','PageView');`}
    </Script>
  );
}
